import { getLoggedUserClaims, getLoggedUserFullName } from 'utils';
import { format } from 'date-fns';

// maps each SAP component's UI field to its backend CIDR key; sap-mgmt has no CIDR fields
const SAP_COMPONENT_PAYLOAD_CIDR_KEYS = {
    'sap-app': { databaseCidr: 'databaseCIDR', managementCidr: 'managementCIDR', sharedFsSubnetCidr: 'sharedFSSSubnetsCIDR' },
    'sap-db': { applicationCidr: 'applicationCIDR', managementCidr: 'managementCIDR' },
    'sap-mgmt': {},
};

const getSapNsgComponents = (instanceDetail) =>
    (instanceDetail?.componentTypes || []).map((componentType) => {
        const componentValues = instanceDetail?.components?.[componentType] || {};
        const cidrKeyMap = SAP_COMPONENT_PAYLOAD_CIDR_KEYS[componentType] || {};
        const cidrFields = Object.entries(cidrKeyMap).reduce((acc, [feField, payloadKey]) => {
            acc[payloadKey] = componentValues?.[feField] || '';
            return acc;
        }, {});
        return {
            componentType,
            networkSecurityGroupName: componentValues?.nsgName || '',
            networkSecurityGroupId: componentValues?.nsgId || '',
            applicationSecurityGroupName: componentValues?.asgName || '',
            applicationSecurityGroupId: componentValues?.asgId || '',
            ...cidrFields,
        };
    });

// only SAP resources carry componentTypes/nsgLandscape/nsgComponents; all other persona types are unaffected
export const getSapCatalogConfig = (instanceDetail) =>
    instanceDetail?.personaType === 'SAP'
        ? {
              componentTypes: instanceDetail?.componentTypes || [],
              nsgLandscape: instanceDetail?.nsgLandscape || null,
              subscriptionName: instanceDetail?.subscriptionDetails?.subscriptionName || null,
              nsgComponents: getSapNsgComponents(instanceDetail),
          }
        : {};

// reverse of getSapNsgComponents: rebuilds the form's componentTypes/nsgLandscape/components from a stored catalogConfig
export const getSapInstanceFromCatalogConfig = (catalogConfig) => {
    if (catalogConfig?.personaType !== 'SAP') return {};
    const components = {};
    (catalogConfig?.nsgComponents || []).forEach((component) => {
        const cidrKeyMap = SAP_COMPONENT_PAYLOAD_CIDR_KEYS[component?.componentType] || {};
        const cidrFields = Object.entries(cidrKeyMap).reduce((acc, [feField, payloadKey]) => {
            acc[feField] = component?.[payloadKey] || '';
            return acc;
        }, {});
        components[component?.componentType] = {
            nsgName: component?.networkSecurityGroupName || '',
            nsgId: component?.networkSecurityGroupId || '',
            asgName: component?.applicationSecurityGroupName || '',
            asgId: component?.applicationSecurityGroupId || '',
            // seeds the ASG dropdown with the previously saved selection until a fresh NSG search replaces it
            asgOptions: component?.applicationSecurityGroupId
                ? [{ id: component?.applicationSecurityGroupId, name: component?.applicationSecurityGroupName || '' }]
                : [],
            ...cidrFields,
        };
    });
    return {
        componentTypes: catalogConfig?.componentTypes || [],
        nsgLandscape: catalogConfig?.nsgLandscape || '',
        subscriptionDetails: catalogConfig?.subscriptionName ? { subscriptionName: catalogConfig?.subscriptionName } : null,
        components,
    };
};

export const getPayload = ({ order = [], catalogCode, catalogServiceId }) => {
    const userClaims = getLoggedUserClaims();
    return {
        requestorGpid: userClaims?.accessToken?.claims?.gpid,
        applicationOwnerId: order?.appOwnerDetails?.gpid,
        entityDetails: {
            name: order?.appProfile,
            sector: order?.entityDetails?.sector || null,
        },
        estimatedCosts: order?.estimatedCost,
        catalogServices: order.instanceDetails.map((instanceDetail) => ({
            ...(catalogServiceId && { catalogServiceId }),
            cloudProvider: order?.cloudProvider,
            cloudService: order?.cloudService,
            catalogServiceCode: catalogCode,
            catalogType: order?.catalogType,
            sector: order?.sector || null,
            estimatedCost: order?.initCost,
            quantity: 1,
            catalogConfig: {
                personaType: instanceDetail?.personaType || null,
                ...(instanceDetail?.personaType === 'SAP' ? getSapCatalogConfig(instanceDetail) : { sourceName: instanceDetail?.sourceName }),
                sector: order?.sector || null,
                environment: order?.environment || null,
                region: order?.regionDisplayname || null,
                justification: order?.justification || null,
                dataClassification: order?.dataClassification || null,
                isaNumber: order?.isaNumber || null,
                tags: {
                    Environment: order?.environment,
                    ApplicationService: order?.appProfile,
                    Requestedby: getLoggedUserFullName(),
                    Sector: order?.sector,
                    Servicename: order?.appProfile,
                    Provisioneddate: format(new Date(), 'yyyy-MM-dd'),
                },
            },
        })),
    };
};
export default getPayload;
